import { Tinode, Drafty } from "tinode-sdk";
import {
  parseHostForSDK,
} from "../utils/tinodeHostParser";
import { parseTinodeError } from "../utils/tinodeErrorHelper";

const API_KEY =
  process.env.REACT_APP_TINODE_API_KEY || "AQEAAAABAAD_rAp4DJh05a1HAwFT3A6K";

export const TINODE_CONTACT_LIMIT = 5000;

const HOST_RAW = process.env.REACT_APP_TINODE_HOST;
if (!HOST_RAW) {
  throw new Error(
    "REACT_APP_TINODE_HOST is required. Please set it in your .env file (e.g., REACT_APP_TINODE_HOST=example.com:6060)",
  );
}
const HOST = parseHostForSDK(HOST_RAW);

const parseHost = (host) => {
  return host.replace(/^wss?:\/\//, "").split(":")[0];
};

const parsePort = (host) => {
  const match = host.match(/:(\d+)/);
  return match ? parseInt(match[1]) : null;
};

const hostname = parseHost(HOST);
const port = parsePort(HOST);
if (!port) {
  throw new Error(
    `Port is required in REACT_APP_TINODE_HOST. Expected format: hostname:port (e.g., example.com:6060)`,
  );
}

export const setupTinode = (
  serverAddress,
  secureConnection,
  transport,
  locale,
  persistentCache,
  onSetupCompleted,
) => {
  const cleanHost = parseHostForSDK(serverAddress);

  let finalHost = cleanHost;

  if (finalHost.match(/^(wss?|https?):/i)) {
    finalHost = finalHost.replace(/^(wss?|https?):\/\/+/, "");
  }

  if (finalHost.startsWith("ws/") || finalHost.startsWith("ws://")) {
    finalHost = finalHost.replace(/^ws\/?\/?/, "");
  }

  if (finalHost.includes("/")) {
    finalHost = finalHost.split("/")[0];
  }

  if (!finalHost.match(/^[a-zA-Z0-9.\-]+:\d+$/)) {
    throw new Error(
      `Invalid server address: "${serverAddress}". Expected format: hostname:port (e.g., example.com:6060)`,
    );
  }

  const tinode = new Tinode(
    {
      appName: "OLIN-PSEF",
      host: finalHost,
      apiKey: API_KEY,
      transport: transport || "websocket",
      secure: secureConnection || false,
      persist: persistentCache || false,
      timeout: 30000,
    },
    onSetupCompleted,
  );

  tinode.setHumanLanguage(locale || "en-US");

  if (process.env.NODE_ENV === "development") {
    tinode.enableLogging(true, true);
  }

  return tinode;
};

export const checkServerStatus = async () => {
  try {
    const httpUrl = `http://${hostname}:${port}/`;
    const response = await fetch(httpUrl, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-cache",
    });
    return true;
  } catch (error) {
    console.warn("Server status check failed:", error);
    return false;
  }
};

export const normalizeUsername = (fullname) => {
  if (!fullname) return "";
  return fullname
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");
};

export const generateTinodeSecret = (email, password) => {
  if (!email || !password) {
    throw new Error(
      "Email and password are required to generate Tinode secret",
    );
  }
  return btoa(`${email}:${password}`);
};

export const generateTinodeSecretFromFullname = (
  fullname,
  password,
  email = "",
) => {
  const username =
    normalizeUsername(fullname) ||
    (email ? email.split("@")[0].toLowerCase() : "");
  if (!username || !password) {
    throw new Error(
      "Fullname and password are required to generate Tinode secret",
    );
  }
  return btoa(`${username}:${password}`);
};

export const storeAuthToken = (token) => {
  try {
    if (token && token.token) {
      const currentUserId = localStorage.getItem("user_id") || null;
      const currentRole = localStorage.getItem("role") || null;

      localStorage.setItem(
        "tinode_auth_token",
        JSON.stringify({
          token: token.token,
          expires: token.expires ? new Date(token.expires).toISOString() : null,
          // Ikat token ke user & role saat ini untuk mencegah
          // kebocoran identitas antar-tab / antar-role.
          userId: currentUserId,
          role: currentRole,
        }),
      );
    }
  } catch (error) {
    console.warn("Failed to store auth token:", error);
  }
};

export const getStoredAuthToken = () => {
  try {
    const stored = localStorage.getItem("tinode_auth_token");
    if (!stored) {
      return null;
    }
    const token = JSON.parse(stored);

    // Validasi masa berlaku token.
    if (token.expires) {
      const expires = new Date(token.expires);
      if (expires < new Date()) {
        localStorage.removeItem("tinode_auth_token");
        return null;
      }
    }

    // Validasi kecocokan user & role saat ini.
    const currentUserId = localStorage.getItem("user_id") || null;
    const currentRole = localStorage.getItem("role") || null;

    const userMismatch =
      token.userId && currentUserId && token.userId !== currentUserId;
    const roleMismatch =
      token.role && currentRole && token.role !== currentRole;

    if (userMismatch || roleMismatch) {
      localStorage.removeItem("tinode_auth_token");
      return null;
    }

    return token;
  } catch (error) {
    console.warn("Failed to get stored auth token:", error);
  }
  return null;
};

export const clearAuthToken = () => {
  try {
    localStorage.removeItem("tinode_auth_token");
  } catch (error) {
    console.warn("Failed to clear auth token:", error);
  }
};

export const connectAndLogin = async (tinode, secret = null, token = null) => {
  if (tinode.isAuthenticated()) {
    return tinode;
  }

  if (!tinode.isConnected()) {
    try {
      await new Promise((resolve, reject) => {
        const originalOnConnect = tinode.onConnect;
        const originalOnError = tinode.onError;

        let settled = false;

        const cleanup = () => {
          tinode.onConnect = originalOnConnect;
          if (originalOnError) {
            tinode.onError = originalOnError;
          }
        };

        const onConnectSuccess = () => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve();
        };

        const onConnectError = (err) => {
          if (settled) return;
          if (tinode.isConnected()) {
            settled = true;
            cleanup();
            resolve();
            return;
          }
          settled = true;
          cleanup();
          reject(err);
        };

        tinode.onConnect = () => {
          if (originalOnConnect) originalOnConnect();
          onConnectSuccess();
        };

        const timeout = setTimeout(() => {
          if (!settled) {
            if (tinode.isConnected()) {
              onConnectSuccess();
            } else {
              onConnectError(new Error("Connection timeout after 10 seconds"));
            }
          }
        }, 10000);

        const connectPromise = tinode.connect();

        if (connectPromise && typeof connectPromise.then === "function") {
          connectPromise
            .then(() => {
              clearTimeout(timeout);
              onConnectSuccess();
            })
            .catch((err) => {
              clearTimeout(timeout);
              if (tinode.isConnected()) {
                onConnectSuccess();
              } else {
                onConnectError(err);
              }
            });
        }
      });

      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!tinode.isConnected()) {
        throw new Error("Connection established but WebSocket not ready");
      }
    } catch (connectError) {
      if (tinode.isConnected()) {
      } else {
        const errorMsg = parseTinodeError(connectError);
        throw new Error(errorMsg);
      }
    }
  }

  if (tinode.isAuthenticated()) {
    return tinode;
  }

  try {
    if (token) {
      const tokenStr = token.token || token;
      await tinode.loginToken(tokenStr);
    } else if (secret) {
      await tinode.login("basic", secret, null);
    } else {
      throw new Error("No credentials provided");
    }
  } catch (loginError) {
    if (
      loginError?.code === 409 ||
      loginError?.message?.includes("already authenticated") ||
      loginError?.text === "already authenticated"
    ) {
      return tinode;
    }
    throw loginError;
  }

  const authToken = tinode.getAuthToken();
  if (authToken?.token) {
    const { storeAuthToken } = await import("./tinodeApi");
    storeAuthToken(authToken);
  }

  return tinode;
};

export const subscribeToMeTopic = async (tinode) => {
  const me = tinode.getMeTopic();
  if (!me) {
    throw new Error("Me topic not available");
  }

  const buildFullContactQuery = () =>
    me
      .startMetaQuery()
      .withSub(undefined, TINODE_CONTACT_LIMIT)
      .withDesc()
      .withTags()
      .withCred()
      .build();

  if (me.isSubscribed()) {
    await me.getMeta(buildFullContactQuery());
    console.info("[Tinode] me topic full contact sync completed", {
      limit: TINODE_CONTACT_LIMIT,
    });
    return me;
  }

  await me.subscribe(buildFullContactQuery());
  console.info("[Tinode] me topic subscribed with contact sync", {
    limit: TINODE_CONTACT_LIMIT,
  });
  return me;
};

export const getTopics = (tinode) => {
  try {
    const me = tinode.getMeTopic();
    if (!me) {
      return [];
    }

    const topicsArray = [];

    try {
      me.contacts((contact) => {
        if (!contact) return;
        const topicName = contact.topic || contact.user || contact.name;
        if (!topicName) return;

        const topic = tinode.getTopic(topicName);
        const displayName =
          topic?.public?.fn || contact.public?.fn || topicName;

        if (topic) {
          const desc = topic.desc || {};
          const unreadCount =
            contact.unread !== undefined && contact.unread !== null
              ? contact.unread
              : topic.unread !== undefined && topic.unread !== null
                ? topic.unread
                : 0;

          topicsArray.push({
            id: topicName,
            name: displayName,
            topic: topicName,
            public: topic.public || contact.public || {},
            private: topic.private || contact.private || {},
            lastMessage:
              topic.lastMsg?.content?.txt ||
              (topic.lastMsg?.content &&
              typeof topic.lastMsg.content === "string"
                ? topic.lastMsg.content
                : null) ||
              null,
            unread: unreadCount,
            created: topic.created || contact.created,
            updated: desc.updated || topic.updated || contact.updated,
            touched:
              desc.touched ||
              contact.touched ||
              desc.updated ||
              topic.updated ||
              contact.updated,
            type:
              topic.getType() ||
              (topicName.startsWith("grp")
                ? "grp"
                : topicName.startsWith("me")
                  ? "me"
                  : topicName.startsWith("fnd")
                    ? "fnd"
                    : "p2p"),
            acs: contact.acs || topic.getAccessMode(),
            online: contact.online || false,
          });
        } else {
          topicsArray.push({
            id: topicName,
            name: displayName,
            topic: topicName,
            public: contact.public || {},
            private: contact.private || {},
            lastMessage: null,
            unread:
              contact.unread !== undefined && contact.unread !== null
                ? contact.unread
                : 0,
            updated: contact.updated,
            touched: contact.touched || contact.updated,
            type: topicName.startsWith("grp")
              ? "grp"
              : topicName.startsWith("me")
                ? "me"
                : topicName.startsWith("fnd")
                  ? "fnd"
                  : "p2p",
            acs: contact.acs,
            online: contact.online || false,
          });
        }
      });
    } catch (contactsError) {
      const subscriptions = me.subscriptions || {};
      const subsEntries =
        subscriptions instanceof Map
          ? Array.from(subscriptions.entries())
          : Object.entries(subscriptions);

      for (const [topicName, sub] of subsEntries) {
        if (!topicName) continue;
        const topic = tinode.getTopic(topicName);
        const displayName = topic?.public?.fn || sub.public?.fn || topicName;

        if (topic) {
          const desc = topic.desc || {};
          const unreadCount =
            sub.unread !== undefined && sub.unread !== null
              ? sub.unread
              : topic.unread !== undefined && topic.unread !== null
                ? topic.unread
                : 0;

          topicsArray.push({
            id: topicName,
            name: displayName,
            topic: topicName,
            public: topic.public || sub.public || {},
            private: topic.private || sub.private || {},
            lastMessage:
              topic.lastMsg?.content?.txt ||
              (topic.lastMsg?.content &&
              typeof topic.lastMsg.content === "string"
                ? topic.lastMsg.content
                : null) ||
              null,
            unread: unreadCount,
            created: topic.created || sub.created,
            updated: desc.updated || topic.updated || sub.updated,
            touched:
              desc.touched ||
              sub.touched ||
              desc.updated ||
              topic.updated ||
              sub.updated,
            type: topic.getType() || "p2p",
            acs: sub.acs || topic.getAccessMode(),
            online: sub.online || false,
          });
        } else {
          topicsArray.push({
            id: topicName,
            name: displayName,
            topic: topicName,
            public: sub.public || {},
            private: sub.private || {},
            lastMessage: null,
            unread:
              sub.unread !== undefined && sub.unread !== null ? sub.unread : 0,
            updated: sub.updated,
            touched: sub.touched || sub.updated,
            type: topicName.startsWith("grp")
              ? "grp"
              : topicName.startsWith("me")
                ? "me"
                : topicName.startsWith("fnd")
                  ? "fnd"
                  : "p2p",
            acs: sub.acs,
            online: sub.online || false,
          });
        }
      }
    }

    topicsArray.sort((a, b) => {
      if (a.unread > 0 && b.unread === 0) return -1;
      if (a.unread === 0 && b.unread > 0) return 1;

      const aTime = a.touched
        ? new Date(a.touched).getTime()
        : a.updated
          ? new Date(a.updated).getTime()
          : 0;
      const bTime = b.touched
        ? new Date(b.touched).getTime()
        : b.updated
          ? new Date(b.updated).getTime()
          : 0;

      return bTime - aTime;
    });

    return topicsArray;
  } catch (error) {
    console.error("Failed to get topics:", error);
    return [];
  }
};

export const subscribeToTopic = async (tinode, topicName) => {
  try {
    if (!topicName) {
      throw new Error("Topic name is required");
    }

    let topic = tinode.getTopic(topicName);
    if (!topic) {
      topic = tinode.initTopic(topicName);
    }

    if (topic.isSubscribed()) {
      return topic;
    }

    const metaQuery = topic
      .startMetaQuery()
      .withSub()
      .withDesc()
      .withAux()
      .build();

    await topic.subscribe(metaQuery);
    return topic;
  } catch (error) {
    console.error("Failed to subscribe to topic:", error);
    throw error;
  }
};

export const subscribeToTopicWithMessages = async (
  tinode,
  topicName,
  messageLimit = null,
) => {
  try {
    if (!topicName) {
      throw new Error("Topic name is required");
    }

    let topic = tinode.getTopic(topicName);
    if (!topic) {
      topic = tinode.initTopic(topicName);
    }

    if (topic.isSubscribed()) {
      const topicSeq = topic.seq || 0;
      let hasStoredMessages = false;
      let storedMessageCount = 0;

      try {
        if (topic.allMessages) {
          topic.allMessages(() => {
            storedMessageCount++;
            hasStoredMessages = true;
          });
        }
      } catch (err) {
        console.log(`ℹ️ Topic ${topicName} allMessages not available yet`);
      }

      const limit = messageLimit || 100;

      let metaQuery;
      if (!hasStoredMessages || topicSeq === 0) {
        console.log(
          `🔄 Requesting all messages for topic ${topicName} (no stored messages, limit: ${limit})`,
        );
        metaQuery = topic
          .startMetaQuery()
          .withData(undefined, undefined, limit)
          .build();
      } else {
        console.log(
          `🔄 Requesting newer messages for topic ${topicName} (has ${storedMessageCount} stored, limit: ${limit})`,
        );
        metaQuery = topic.startMetaQuery().withLaterData(limit).build();
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("getMeta timeout")), 5000);
      });

      try {
        await Promise.race([topic.getMeta(metaQuery), timeoutPromise]);
        console.log(`✅ getMeta completed for subscribed topic: ${topicName}`);
      } catch (metaErr) {
        if (
          metaErr?.message?.includes("timeout") ||
          metaErr?.code === 504 ||
          metaErr?.text?.includes("timeout")
        ) {
          console.warn("⚠️ getMeta timeout for subscribed topic (non-fatal)");
        } else if (metaErr?.code === 204 || metaErr?.text === "no content") {
          if (!hasStoredMessages && topicSeq > 0) {
            console.log(
              `🔄 getMeta returned 204 but topic has seq ${topicSeq}, retrying without 'since' to force reload`,
            );
            try {
              const retryQuery = topic
                .startMetaQuery()
                .withData(undefined, undefined, limit)
                .build();
              const retryTimeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error("getMeta timeout")), 5000);
              });
              await Promise.race([
                topic.getMeta(retryQuery),
                retryTimeoutPromise,
              ]);
              console.log(
                `✅ Retry getMeta completed for topic: ${topicName} (forced reload after 204)`,
              );
            } catch (retryErr) {
              console.warn("⚠️ Retry getMeta failed (non-fatal):", retryErr);
            }
          } else {
            console.log(`ℹ️ Topic ${topicName} has no content (204)`);
          }
        } else {
          console.warn(
            "⚠️ Failed to getMeta for subscribed topic (non-fatal):",
            metaErr,
          );
        }
      }

      return topic;
    }

    const limit = messageLimit || undefined;
    const metaQuery = topic
      .startMetaQuery()
      .withLaterDesc()
      .withLaterSub()
      .withLaterData(limit)
      .withAux()
      .build();

    await topic.subscribe(metaQuery);
    return topic;
  } catch (error) {
    const is503Error = 
      error?.code === 503 ||
      error?.text === "Websocket is not connected" ||
      error?.message?.includes("Websocket is not connected") ||
      error?.message?.includes("503");
    
    if (is503Error) {
      console.warn(
        "⚠️ Websocket not connected (503) in subscribe, attempting to reconnect...",
      );

      try {
        const { connectTinodeInstance, attemptTinodeRelogin } = await import("./tinodeSingleton");
        
        if (!tinode.isConnected()) {
          await connectTinodeInstance(tinode);
        }
        
        if (!tinode.isAuthenticated()) {
          await attemptTinodeRelogin(tinode);
        }

        await new Promise((r) => setTimeout(r, 500));

        if (tinode.isConnected() && tinode.isAuthenticated()) {
          console.log("✅ Reconnected, retrying subscribe...");
          try {
            let topic = tinode.getTopic(topicName);
            if (!topic) {
              topic = tinode.initTopic(topicName);
            }
            
            const limit = messageLimit || undefined;
            const metaQuery = topic
              .startMetaQuery()
              .withLaterDesc()
              .withLaterSub()
              .withLaterData(limit)
              .withAux()
              .build();

            await topic.subscribe(metaQuery);
            return topic;
          } catch (retryError) {
            console.error("❌ Retry subscribe failed after reconnect:", retryError);
            throw retryError;
          }
        } else {
          throw new Error("Failed to reconnect WebSocket");
        }
      } catch (reconnectError) {
        console.error("❌ Reconnect failed:", reconnectError);
        const wsError = new Error(
          "WebSocket connection lost. Please refresh the page.",
        );
        wsError.code = 503;
        wsError.text = "Websocket is not connected";
        wsError.originalError = error;
        throw wsError;
      }
    }

    if (
      error &&
      typeof error === "object" &&
      (error.code === 401 ||
        error.text === "authentication required" ||
        error.message?.includes("authentication required"))
    ) {
      console.warn(
        "⚠️ Authentication required (401) in subscribe, attempting to re-authenticate...",
      );

      const { attemptTinodeRelogin } = await import("./tinodeSingleton");
      const relogged = await attemptTinodeRelogin(tinode);
      
      if (relogged && tinode.isAuthenticated()) {
        console.log("✅ Re-authenticated, retrying subscribe...");
        try {
          let topic = tinode.getTopic(topicName);
          if (!topic) {
            topic = tinode.initTopic(topicName);
          }
          
          const limit = messageLimit || undefined;
          const metaQuery = topic
            .startMetaQuery()
            .withLaterDesc()
            .withLaterSub()
            .withLaterData(limit)
            .withAux()
            .build();

          await topic.subscribe(metaQuery);
          return topic;
        } catch (retryError) {
          console.error("❌ Retry subscribe failed after re-auth:", retryError);
          throw retryError;
        }
      } else {
        console.warn("⚠️ Re-authentication failed, cannot retry subscribe");
        const authError = new Error(
          "Authentication required. Please refresh the page or login again.",
        );
        authError.code = 401;
        authError.text = "authentication required";
        authError.originalError = error;
        throw authError;
      }
    }

    console.error("Failed to subscribe to topic with messages:", error);
    throw error;
  }
};

export const loadOlderMessages = async (
  tinode,
  topicName,
  messageLimit = 24,
  beforeSeq = null,
) => {
  try {
    if (!topicName) {
      throw new Error("Topic name is required");
    }

    const topic = tinode.getTopic(topicName);
    if (!topic || !topic.isSubscribed()) {
      throw new Error("Topic not found or not subscribed");
    }

    const limit = messageLimit || 100;
    if (topic.getMessagesPage && beforeSeq) {
      await topic.getMessagesPage(limit, null, undefined, beforeSeq, false);
    } else {
      const metaQuery = topic
        .startMetaQuery()
        .withData(undefined, beforeSeq, limit)
        .build();
      await topic.getMeta(metaQuery);
    }

    return topic;
  } catch (error) {
    console.error("Failed to load older messages:", error);
    throw error;
  }
};

export const leaveTopic = async (tinode, topicName, unsub = false) => {
  try {
    if (!topicName || !tinode.isTopicCached(topicName)) {
      return;
    }

    const topic = tinode.getTopic(topicName);
    if (!topic) {
      return;
    }

    if (topic.isSubscribed()) {
      try {
        await topic.leave(unsub);
      } catch (err) {}
    }

    if (topic) {
      topic.onData = undefined;
      topic.onAllMessagesReceived = undefined;
      topic.onInfo = undefined;
      topic.onMetaDesc = undefined;
      topic.onSubsUpdated = undefined;
      topic.onPres = undefined;
      topic.onAuxUpdated = undefined;
    }
  } catch (error) {
    console.error("Failed to leave topic:", error);
    throw error;
  }
};

export const sendMessage = async (
  tinode,
  topicName,
  message,
  customType = null,
  data = null,
  head = null,
) => {
  try {
    if (!topicName || !message) {
      throw new Error("Topic name and message are required");
    }

    let topic = tinode.getTopic(topicName);
    if (!topic) {
      topic = tinode.initTopic(topicName);
    }

    if (!topic.isSubscribed()) {
      const metaQuery = topic
        .startMetaQuery()
        .withSub()
        .withDesc()
        .withAux()
        .build();
      await topic.subscribe(metaQuery);
    }

    let msgContent = message;

    if (customType) {
      msgContent = Drafty.init(message || "");
      if (!msgContent) {
        msgContent = { txt: message || "" };
      }

      if (!msgContent.ent) {
        msgContent.ent = [];
      }

      msgContent.ent.push({
        tp: "EX",
        data: {
          mime: "application/json",
          val: JSON.stringify({
            type: customType,
            ...(data || {}),
          }),
        },
      });
    }

    const pubMsg = topic.createMessage(msgContent, false);

    if (head) {
      pubMsg.head = Object.assign(pubMsg.head || {}, head);
    }

    const ctrl = await topic.publishDraft(pubMsg, Promise.resolve());

    if (topic.isArchived()) {
      topic.archive(false);
    }

    return ctrl;
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      (error.code === 401 ||
        error.text === "authentication required" ||
        error.message?.includes("authentication required"))
    ) {
      console.warn(
        "⚠️ Authentication required (401), attempting to re-authenticate...",
      );

      clearAuthToken();

      try {
        const storedToken = getStoredAuthToken();
        if (storedToken?.token) {
          clearAuthToken();
        }

        console.warn("⚠️ Please re-authenticate. Token may have expired.");
      } catch (reauthError) {
        console.error("Failed to re-authenticate:", reauthError);
      }

      const authError = new Error(
        "Authentication required. Please refresh the page or login again.",
      );
      authError.code = 401;
      authError.text = "authentication required";
      authError.originalError = error;
      throw authError;
    }

    let errorMessage = "Failed to send message";

    if (error && typeof error === "object") {
      if (
        error.code === 504 ||
        error.text === "timeout" ||
        error.message?.includes("timeout")
      ) {
        errorMessage =
          "Message send timeout. The message may still be processing. Please try again.";
      } else if (
        error.code === 413 ||
        error.message?.includes("too large") ||
        error.text?.includes("too large")
      ) {
        errorMessage = "Image is too large. Please use a smaller image.";
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.text) {
        errorMessage = error.text;
      } else if (error.code) {
        errorMessage = `Error ${error.code}: ${error.text || "Unknown error"}`;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error && typeof error === "object") {
      if (error.type && (error.type === "error" || error.type === "close")) {
        errorMessage =
          "WebSocket connection failed. Please check if Tinode server is running.";
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.text) {
        errorMessage = error.text;
      } else {
        const errorStr = String(error);
        if (errorStr === "[object Event]" || errorStr === "[object Object]") {
          errorMessage =
            "WebSocket connection failed. Please check if Tinode server is running.";
        } else {
          errorMessage = errorStr;
        }
      }
    } else {
      const errorStr = String(error);
      if (errorStr === "[object Event]") {
        errorMessage =
          "WebSocket connection failed. Please check if Tinode server is running.";
      } else {
        errorMessage = errorStr;
      }
    }

    const nonFatalError = new Error(errorMessage);
    nonFatalError.code = error?.code;
    nonFatalError.text = error?.text;
    nonFatalError.originalError = error;

    console.error("Failed to send message:", errorMessage, error);
    throw nonFatalError;
  }
};

export default {
  setupTinode,
  checkServerStatus,
  normalizeUsername,
  generateTinodeSecret,
  storeAuthToken,
  getStoredAuthToken,
  clearAuthToken,
  connectAndLogin,
  subscribeToMeTopic,
  getTopics,
  subscribeToTopic,
  subscribeToTopicWithMessages,
  loadOlderMessages,
  sendMessage,
};
